import {emptyArray} from '@packages/utilities/fp/utils';
import {append} from '@packages/utilities/array';
import {
  type Json,
  getAsJson,
  getObject,
  getString,
} from '@packages/utilities/json';
import {type Pair, pair, getSecond} from '@packages/utilities/Pair';
import {
  type RpcConfig,
  type JsonRpcRequest,
  type RpcAction,
  type RpcResponder,
  type JsonRpcResponse,
  type ResponderHandler,
  unsubscribe,
  createResponse,
  trusted,
} from './types';
import {mkResponder, runResponder, type Responder} from './responder';

export type {Responder} from './responder';

export {JsonRpcClient} from './JsonRpcClient';
export type {JsonRpcClientInterface} from './JsonRpcClient';

export type {RpcAction, Codec, ResponderHandler} from './types';
export {
  trusted,
  requestToJson,
  createRequest,
  createResponse,
  now,
  later,
} from './types';

export type Message = {
  sendTo: WindowProxy;
  message: JsonRpcResponse;
};

export type Outbox = Message[];

export const createRpcMethod = <req, res>(
  val: RpcConfig<req, res>
): RpcAction<req, res> => ({tag: 'Method', val});

export const createRpcSubscription = <req, res>(
  val: RpcConfig<req, res>
): RpcAction<req, res> => ({tag: 'Subscription', val});

export const createRPCResponder = <state, fluxAction, req, res, context>(
  action: RpcAction<req, res>,
  handler: ResponderHandler<state, fluxAction, req, res, context>
): Responder<state, fluxAction> => mkResponder({action, handler});

type RpcResponse<State> = (finalState: State) => JsonRpcResponse;

export type RpcStore<State, Action, Context> = {
  pendingRequests: Array<{
    request: JsonRpcRequest;
    from: WindowProxy;
  }>;
  currentRequest: null | {
    handler: (
      currentState: State,
      currentAction: Action,
      currentContext: Context
    ) => Pair<null | RpcResponse<State>, State>;
    from: WindowProxy;
    requestId: string;
  };
  processedRequests: Array<{
    getValue: RpcResponse<State>;
    sendTo: WindowProxy;
    requestId: string;
  }>;
  subscriptions: Array<{
    id: string;
    getValue: (arg1: State) => JsonRpcResponse;
    sendTo: WindowProxy;
    lastValue: JsonRpcResponse | undefined;
  }>;
};

export type RpcMessageAction =
  | {
      type: 'POST_MESSAGE_RECEIVED';
      payload: MessageEvent;
    }
  | {
      type: 'POST_MESSAGES_SENT';
      payload: Outbox;
    };

export function getRpcRequest(json: Json): JsonRpcRequest | undefined {
  const obj = getObject(json);
  if (obj === undefined) return;
  const {id, method, params, jsonrpc} = obj;
  if (method === undefined) return;
  const methodAsString = getString(method);
  if (methodAsString === undefined) return;
  if (params === undefined) return;
  if (jsonrpc !== '2.0') return;
  if (id === undefined) return;
  const idAsString = getString(id);
  if (idAsString === undefined) return;
  return trusted(obj);
}

type Host<State, action, Context> = {
  reducer: (arg1: State, action: action, context: Context) => State;
  outbox: (arg1: State) => Outbox;
};

export const createHost = <State, Action, Context>(
  withRpcState: (
    state: State
  ) => [
    RpcStore<State, Action, Context>,
    (rpcState: RpcStore<State, Action, Context>) => State,
  ],
  getRpcMessageAction: (action: Action) => null | RpcMessageAction,
  responders: Array<Responder<State, Action>>
): Host<State, Action, Context> => {
  // Make a map for efficient lookups
  const respondersMap = responders.reduce<
    Record<string, Responder<State, Action>>
  >((map, responder) => {
    const name = runResponder((r) => r.action.val.name)(responder);
    map[name] = responder;
    return map;
  }, {});

  const processRpcAction = (state: State, action: RpcMessageAction) => {
    const [rpcState, setRpcState] = withRpcState(state);
    const {pendingRequests, currentRequest, processedRequests, subscriptions} =
      rpcState;
    if (action.type === 'POST_MESSAGES_SENT') {
      const sentIds = new Set(action.payload.map((m) => m.message.id));
      return setRpcState({
        pendingRequests,
        currentRequest,
        processedRequests: processedRequests.filter(
          (r) => !sentIds.has(r.requestId)
        ),
        subscriptions: subscriptions.map((subscription) => {
          const nextValue = subscription.getValue(state);
          if (nextValue === undefined || nextValue.result === undefined)
            return subscription;
          if (
            subscription.lastValue === undefined ||
            subscription.lastValue.result !== nextValue.result
          ) {
            return {
              ...subscription,
              lastValue: nextValue,
            };
          }
          return subscription;
        }),
      });
    }

    // action.type === 'POST_MESSAGE_RECEIVED'

    const asJson = getAsJson(action.payload.data);
    if (asJson === undefined) return state;
    const request = getRpcRequest(asJson);
    if (request === undefined) return state;

    return setRpcState({
      pendingRequests: append(pendingRequests, {
        request,
        from: action.payload.source as WindowProxy,
      }),
      currentRequest,
      processedRequests,
      subscriptions,
    });
  };

  const processCurrentRequest = (
    state: State,
    action: Action,
    context: Context
  ) => {
    const [rpcState, setRpcState] = withRpcState(state);
    const {pendingRequests, currentRequest, processedRequests, subscriptions} =
      rpcState;

    if (!currentRequest) return state;

    const [getValue, nextState] = currentRequest.handler(
      state,
      action,
      context
    );
    if (!getValue) return nextState;

    return setRpcState({
      pendingRequests,
      currentRequest: null,
      processedRequests: append(processedRequests, {
        getValue,
        sendTo: currentRequest.from,
        requestId: currentRequest.requestId,
      }),
      subscriptions,
    });
  };

  const processPendingRequests = (state: State, context: Context): State => {
    const [rpcState, setRpcState] = withRpcState(state);
    const {pendingRequests, currentRequest, processedRequests, subscriptions} =
      rpcState;

    // We have another request in flight so bail
    if (currentRequest) return state;

    // Nothing to do so bail
    if (pendingRequests.length < 1) return state;

    const [currReq, ...remaining] = pendingRequests;
    const {request, from} = currReq || {};
    if (!request || !from) return state;

    if (request.method === unsubscribe.val.name) {
      const requestId = request.id;
      const unsubscribeRequest = unsubscribe.val.req.decode(request.params);

      if (!unsubscribeRequest) return state;

      return processPendingRequests(
        setRpcState({
          pendingRequests: remaining,
          currentRequest,
          processedRequests: append(processedRequests, {
            sendTo: from,
            requestId,
            getValue: () =>
              createResponse(requestId, unsubscribe, {
                id: unsubscribeRequest.id,
              }),
          }),
          subscriptions: rpcState.subscriptions.filter(
            (subscription) =>
              !(
                subscription.id === unsubscribeRequest.id &&
                subscription.sendTo === from
              )
          ),
        }),
        context
      );
    }

    const responder = respondersMap[request.method];
    if (!responder) return state;

    return processPendingRequests(
      runResponder(
        <req, res>(r: RpcResponder<State, Action, req, res, Context>) => {
          if (r.action.val.name === request.method) {
            const params = r.action.val.req.decode(request.params);
            // If the req.decode logic rejects the params, it will return undefined,
            // and therefore we should remove the current request from the pendingRequests
            // and continue processing the next request
            if (params === undefined) {
              const setNextRpcState = getSecond(withRpcState(state));
              return setNextRpcState({
                pendingRequests: remaining,
                currentRequest,
                processedRequests,
                subscriptions,
              });
            }
            const rpcRequestId = request.id;

            const [result, nextState] = r.handler(state, params, context);
            const setNextRpcState = getSecond(withRpcState(nextState));

            if (result.tag === 'Now') {
              const getValue = (finalState: State) =>
                createResponse<req, res>(
                  rpcRequestId,
                  r.action,
                  result.val(finalState)
                );

              if (r.action.tag === 'Method')
                return setNextRpcState({
                  pendingRequests: remaining,
                  currentRequest,
                  processedRequests: append(processedRequests, {
                    getValue,
                    sendTo: from,
                    requestId: rpcRequestId,
                  }),
                  subscriptions,
                });

              // r.action.tag === 'Subscription
              return setNextRpcState({
                pendingRequests: remaining,
                currentRequest,
                processedRequests,
                subscriptions: append(subscriptions, {
                  id: rpcRequestId,
                  getValue,
                  sendTo: from,
                  lastValue: undefined,
                }),
              });
            }

            // result.tag === 'Later

            // Assume subscriptions are not async
            if (r.action.tag === 'Subscription') return state;

            const handler = (currentState: State, action: Action) => {
              const [response, updatedState] = result.val(currentState, action);

              if (!response) return pair(null, updatedState);

              const getValue = (finalState: State) =>
                createResponse<req, res>(
                  rpcRequestId,
                  r.action,
                  response(finalState)
                );

              return pair(getValue, updatedState);
            };

            return setNextRpcState({
              pendingRequests: remaining,
              currentRequest: {handler, from, requestId: rpcRequestId},
              processedRequests,
              subscriptions,
            });
          }

          return state;
        }
      )(responder),
      context
    );
  };

  const processRequests = (
    initialState: State,
    action: Action,
    context: Context
  ): State => {
    const state = processCurrentRequest(initialState, action, context);
    return processPendingRequests(state, context);
  };

  return {
    reducer: (initialState: State, action: Action, context: Context): State => {
      const rpcAction = getRpcMessageAction(action);
      const state = rpcAction
        ? processRpcAction(initialState, rpcAction)
        : initialState;
      return processRequests(state, action, context);
    },
    outbox: (state: State): Message[] => {
      const [rpcState] = withRpcState(state);

      const replies: Outbox = rpcState.subscriptions.flatMap((subscription) => {
        const nextValue = subscription.getValue(state);
        if (
          subscription.lastValue !== undefined &&
          subscription.lastValue.result === nextValue.result
        )
          return emptyArray;

        return [
          {
            message: nextValue,
            sendTo: subscription.sendTo,
          },
        ];
      });
      const responses = rpcState.processedRequests.map(
        ({getValue, sendTo}) => ({message: getValue(state), sendTo})
      );
      return replies.concat(responses);
    },
  };
};

export const getInitialRpcState = <State, Action, Context>(): RpcStore<
  State,
  Action,
  Context
> => ({
  pendingRequests: emptyArray,
  currentRequest: null,
  processedRequests: emptyArray,
  subscriptions: emptyArray,
});
